/**
 * GCC tiered delivery messaging & reactive progress bar — cart drawer.
 *
 * Fully reactive and multi-currency aware (Shopify Markets, Native Currency).
 * Automatically updates tier rates, thresholds, remaining amounts, and progress bar
 * to match whatever currency is active across the entire website.
 */
(function () {
  const selectors = {
    message: '[data-gcc-shipping-message]',
    tier: '[data-gcc-tier]',
    amount: '[data-gcc-amount]',
    tierAmount: '[data-gcc-tier-amount]',
    rate: '[data-gcc-rate]',
    progress: '[data-gcc-message-progress]',
    cartTotal: '[data-cart-total]',
    priceHolder: '[data-cart-price-holder]',
    cartDrawer: '[data-cart-drawer]',
    cartDrawerBody: '[data-cart-drawer-body]',
    itemsQty: '[data-cart-items-qty]',
    targetNotice: '.cart-message__target-notice',
  };

  const attributes = {
    limits: 'data-gcc-limits',
    rates: 'data-gcc-rates',
    limitsChf: 'data-gcc-limits-chf',
    ratesChf: 'data-gcc-rates-chf',
    currency: 'data-gcc-currency',
    currencyRate: 'data-gcc-currency-rate',
    cartTotal: 'data-cart-total',
    tier: 'data-gcc-tier',
    tierAmount: 'data-gcc-tier-amount',
    rate: 'data-gcc-rate',
    template: 'data-gcc-template',
  };

  const classes = {
    isHidden: 'is-hidden',
    isSuccess: 'is-success',
  };

  const SWISS_AND_EU_COUNTRIES = [
    'CH', 'LI',
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    'GB', 'NO', 'IS', 'MC', 'AD', 'SM', 'VA'
  ];
  const SWISS_AND_EU_CURRENCIES = ['CHF', 'EUR', 'GBP', 'DKK', 'SEK', 'NOK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'ISK'];

  const BASE_RATES = {
    CHF: 1.0,
    USD: 1.15,
    EUR: 1.05,
    GBP: 0.90,
    SAR: 4.25,
    AED: 4.15,
    KWD: 0.35,
    QAR: 4.12,
    BHD: 0.43,
    OMR: 0.44,
    CAD: 1.55,
    AUD: 1.75,
    JPY: 170.0,
    INR: 95.0,
    SGD: 1.55,
    NZD: 1.90,
    HKD: 9.00,
    SEK: 12.0,
    NOK: 12.0,
    DKK: 7.80,
    PLN: 4.50,
    CZK: 26.0,
    HUF: 420.0,
    ILS: 4.20,
    TRY: 38.0,
    ZAR: 20.0,
    BRL: 6.00,
    MXN: 22.0,
    CNY: 8.30,
    KRW: 1550.0,
  };

  const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CHF: 'CHF',
    SAR: 'SAR',
    AED: 'AED',
    KWD: 'KWD',
    QAR: 'QAR',
    BHD: 'BHD',
    OMR: 'OMR',
    CAD: 'CA$',
    AUD: 'AU$',
    JPY: '¥',
    INR: '₹',
    SGD: 'SG$',
    NZD: 'NZ$',
    HKD: 'HK$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    ILS: '₪',
    TRY: '₺',
    ZAR: 'R',
    BRL: 'R$',
    MXN: 'MX$',
    CNY: '¥',
    KRW: '₩',
  };

  // Base CHF amounts in cents
  const BASE_LIMITS_CHF = [25000, 35000, 40000]; // 250 CHF, 350 CHF, 400 CHF
  const BASE_RATES_CHF = [10000, 7500, 6000, 5000]; // 100 CHF, 75 CHF, 60 CHF, 50 CHF

  let frame = null;
  let observedPriceHolder = null;
  let lastCheckedState = '';

  const observer = new MutationObserver(() => schedule());

  // Safe DOM Mutation Helpers to eliminate layout thrashing & cursor flickering
  function safeSetStyleDisplay(el, val) {
    if (el && el.style.display !== val) {
      el.style.display = val;
    }
  }

  function safeToggleClass(el, cls, force) {
    if (el && el.classList.contains(cls) !== !!force) {
      el.classList.toggle(cls, !!force);
    }
  }

  function safeSetInnerHTML(el, html) {
    if (el && el.innerHTML !== html) {
      el.innerHTML = html;
    }
  }

  function safeSetProgress(el, percent) {
    if (!el) return;
    const pStr = `${percent}%`;
    if (el.style.getPropertyValue('--progress-width') !== pStr) {
      el.style.setProperty('--progress-width', pStr);
    }
    const valStr = String(percent);
    if (el.getAttribute('value') !== valStr) {
      el.setAttribute('value', valStr);
    }
  }

  /**
   * Dynamically detect the active currency used across the site
   */
  function getActiveCurrency(serverCurrency) {
    if (typeof window.getActiveWebsiteCurrency === 'function') {
      return window.getActiveWebsiteCurrency(serverCurrency);
    }
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active.toUpperCase();
    }
    return (serverCurrency || 'CHF').toUpperCase();
  }

  /**
   * Check if active destination is International (outside Switzerland & Europe)
   */
  function isInternationalActive() {
    if (typeof window.isInternationalWebsiteActive === 'function') {
      return window.isInternationalWebsiteActive();
    }

    try {
      const countryInput = document.querySelector('input[name="country_code"], [data-popout-input][name="country_code"]');
      if (countryInput && countryInput.value) {
        const cCode = countryInput.value.trim().toUpperCase();
        return !SWISS_AND_EU_COUNTRIES.includes(cCode);
      }
    } catch (e) {}

    try {
      if (window.Shopify && window.Shopify.country) {
        const cCode = String(window.Shopify.country).trim().toUpperCase();
        return !SWISS_AND_EU_COUNTRIES.includes(cCode);
      }
    } catch (e) {}

    const cur = getActiveCurrency();
    if (!SWISS_AND_EU_CURRENCIES.includes(cur)) return true;
    if (cur === 'CHF' || cur === 'EUR' || cur === 'GBP') return false;

    return false;
  }

  window.isInternationalWebsiteActive = isInternationalActive;
  window.isGccWebsiteActive = isInternationalActive;

  /**
   * Get exchange rate against CHF (1.0)
   */
  function getExchangeRate(currency) {
    const cur = (currency || getActiveCurrency()).toUpperCase();

    if (cur === 'CHF') {
      return 1.0;
    }

    if (typeof window.getWebsiteExchangeRate === 'function') {
      return window.getWebsiteExchangeRate(cur);
    }

    if (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active === cur &&
      typeof window.Shopify.currency.rate === 'number'
    ) {
      return window.Shopify.currency.rate;
    }

    return BASE_RATES[cur] || 1.0;
  }

  /**
   * Format money value cleanly for the active currency
   */
  function formatMoney(cents, currency) {
    const amount = Math.ceil(Math.max(cents, 0) / 100);
    const cur = (currency || getActiveCurrency()).toUpperCase();

    if (typeof window.formatWebsiteMoney === 'function') {
      return window.formatWebsiteMoney(amount, cur);
    }

    const symbol = CURRENCY_SYMBOLS[cur] || cur;
    if (cur === 'USD') return `$ ${amount}`;
    if (cur === 'EUR') return `€ ${amount}`;
    if (cur === 'GBP') return `£ ${amount}`;
    if (cur === 'CHF') return `CHF ${amount}`;
    if (cur === 'BHD') return `${amount} BD`;
    if (cur === 'SAR' || cur === 'AED' || cur === 'KWD' || cur === 'QAR' || cur === 'OMR') {
      return `${cur} ${amount}`;
    }

    return `${symbol} ${amount}`;
  }

  /**
   * Get the current discounted cart subtotal in cents.
   */
  function getCartTotal() {
    const totalElement = document.querySelector(selectors.cartTotal);

    if (totalElement && totalElement.hasAttribute(attributes.cartTotal)) {
      const total = parseInt(totalElement.getAttribute(attributes.cartTotal), 10);
      if (!isNaN(total)) return total;
    }

    // Check if cart has 0 items
    const qtyElement = document.querySelector(selectors.itemsQty);
    if (qtyElement && qtyElement.classList.contains(classes.isHidden)) {
      return 0;
    }

    // Fallback parsing from text
    const priceElement = document.querySelector(selectors.priceHolder);
    if (priceElement) {
      const digits = priceElement.textContent.replace(/[^\d]/g, '');
      if (digits) return parseInt(digits, 10);
    }

    return 0;
  }

  /**
   * Main recalculation function
   */
  function updateMessage(messageEl) {
    if (!messageEl) return;

    const targetInfo = typeof window.getTargetProductCartInfo === 'function' ? window.getTargetProductCartInfo() : {
      hasTargetProduct: false,
      isTargetOnly: false,
      targetProductCount: 0,
      targetProductTotalCents: 0,
      otherItemsCount: 0,
      otherItemsTotalCents: 0,
      totalCents: getCartTotal(),
    };

    const isIntl = isInternationalActive();
    const europeFreeShippingEls = document.querySelectorAll(
      '[data-cart-message][data-shipping-type="europe"], [data-cart-message][data-shipping-type="worldwide"], [data-cart-message][data-limit]'
    );

    // If target product alone, it receives worldwide free shipping, so international tiered delivery is suppressed!
    if (targetInfo.isTargetOnly || !isIntl) {
      safeSetStyleDisplay(messageEl, 'none');
      safeToggleClass(messageEl, classes.isHidden, true);
      europeFreeShippingEls.forEach((el) => {
        safeSetStyleDisplay(el, '');
        safeToggleClass(el, classes.isHidden, false);
      });
      return;
    }

    // International: Show tiered international delivery, hide free shipping bar
    europeFreeShippingEls.forEach((el) => {
      safeSetStyleDisplay(el, 'none');
      safeToggleClass(el, classes.isHidden, true);
    });

    safeSetStyleDisplay(messageEl, '');
    safeToggleClass(messageEl, classes.isHidden, false);

    // Target notice for mixed cart in GCC / International
    const targetNoticeEl = messageEl.querySelector(selectors.targetNotice);
    if (targetNoticeEl) {
      safeToggleClass(targetNoticeEl, classes.isHidden, !(targetInfo.hasTargetProduct && targetInfo.otherItemsCount > 0));
    }

    const activeCurrency = getActiveCurrency();
    const rate = getExchangeRate(activeCurrency);

    // In mixed cart, evaluate tiers ONLY on other items!
    const effectiveTotalCents = (targetInfo.hasTargetProduct && targetInfo.otherItemsCount > 0)
      ? targetInfo.otherItemsTotalCents
      : (targetInfo.totalCents > 0 ? targetInfo.totalCents : getCartTotal());

    // Calculate active limits and delivery rates based on current currency
    const limits = BASE_LIMITS_CHF.map((chf) => Math.round(chf * rate));
    const rates = BASE_RATES_CHF.map((chf) => Math.round(chf * rate));

    const serverCurrency = (messageEl.getAttribute(attributes.currency) || 'CHF').toUpperCase();
    const serverRate = getExchangeRate(serverCurrency);
    const cartTotalBaseChf = effectiveTotalCents / (serverRate || 1.0);
    const activeCartTotalCents = cartTotalBaseChf * rate;

    // Determine current active tier (1 to 4)
    let currentTier = 1;
    if (activeCartTotalCents >= limits[2]) {
      currentTier = 4;
    } else if (activeCartTotalCents >= limits[1]) {
      currentTier = 3;
    } else if (activeCartTotalCents >= limits[0]) {
      currentTier = 2;
    } else {
      currentTier = 1;
    }

    // Update delivery rate displays
    const rateEls = messageEl.querySelectorAll(selectors.rate);
    rateEls.forEach((el) => {
      const tierNum = parseInt(el.getAttribute(attributes.rate), 10);
      if (tierNum >= 1 && tierNum <= 4) {
        const formattedRate = formatMoney(rates[tierNum - 1], activeCurrency);
        safeSetInnerHTML(el, `<span class="money">${formattedRate}</span>`);
      }
    });

    // Update remaining amounts to spend
    const amountEls = messageEl.querySelectorAll(selectors.amount);
    amountEls.forEach((el) => {
      const tierNum = parseInt(el.getAttribute(attributes.tierAmount), 10);
      if (tierNum >= 1 && tierNum <= 3) {
        const targetLimit = limits[tierNum - 1];
        const remaining = Math.max(0, targetLimit - activeCartTotalCents);
        const formattedAmount = formatMoney(remaining, activeCurrency);
        safeSetInnerHTML(el, `<span class="money">${formattedAmount}</span>`);
      }
    });

    // Show only the current active tier message
    const tierEls = messageEl.querySelectorAll(selectors.tier);
    tierEls.forEach((el) => {
      const tierNum = parseInt(el.getAttribute(attributes.tier), 10);
      if (tierNum === currentTier) {
        safeSetStyleDisplay(el, '');
        safeToggleClass(el, classes.isHidden, false);
      } else {
        safeSetStyleDisplay(el, 'none');
        safeToggleClass(el, classes.isHidden, true);
      }
    });

    // Update progress bar
    const maxLimit = limits[2];
    const percent = maxLimit > 0 ? Math.min(100, Math.max(0, Math.round((activeCartTotalCents / maxLimit) * 100))) : 100;

    const progressEls = messageEl.querySelectorAll(selectors.progress);
    progressEls.forEach((pb) => {
      safeSetProgress(pb, percent);
    });

    // Set success class for highest tier
    safeToggleClass(messageEl, classes.isSuccess, currentTier === 4);
  }

  function run() {
    frame = null;

    // Do NOT query template if drawer already exists in DOM
    const messages = Array.from(document.querySelectorAll(selectors.message));
    if (messages.length === 0) {
      const template = document.querySelector('template[data-cart-drawer-template]');
      if (template && template.content) {
        const templateMessages = template.content.querySelectorAll(selectors.message);
        templateMessages.forEach((el) => messages.push(el));
      }
    }

    if (messages.length === 0) return;

    messages.forEach(updateMessage);

    // Ensure price holder is observed
    const priceHolder = document.querySelector(selectors.priceHolder);
    if (priceHolder && priceHolder !== observedPriceHolder) {
      if (observedPriceHolder) {
        try {
          observer.unobserve(observedPriceHolder);
        } catch (e) {}
      }
      observer.observe(priceHolder, {
        childList: true,
        characterData: true,
        subtree: true,
      });
      observedPriceHolder = priceHolder;
    }
  }

  function schedule() {
    if (frame !== null) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(run);
  }

  function checkCurrencyLoop() {
    const cur = getActiveCurrency();
    const isIntl = isInternationalActive();
    const stateKey = `${cur}_${isIntl}`;
    if (stateKey !== lastCheckedState) {
      lastCheckedState = stateKey;
      schedule();
    }
  }

  // Event bindings
  [
    'theme:cart:added',
    'theme:cart:update',
    'theme:cart:loaded',
    'theme:cart:close',
    'theme:cart:open',
    'cart:updated',
    'cart:refresh',
    'currency:change',
    'storage',
  ].forEach((event) => {
    document.addEventListener(event, schedule);
    window.addEventListener(event, schedule);
  });

  // Continuous currency watcher for asynchronous theme / cart changes
  setInterval(checkCurrencyLoop, 200);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
